import { Gas_Viscosity_Options_Enum } from "gql/api";

import { chenRuthGasViscosityCalculator } from "./chenRuthGas";
import { leeGasViscosityCalculator } from "./leeGas";
import { standingsDempseyGasViscosityCalculator } from "./carrKobayashiBurrows-standingsDempsey";
import { carrKobayashiBurrowsGasViscosityCalculator } from "./carrKobayashiBurrows-dib";
import type { GasState, GasViscosityCalculations } from "../d";
import type { Correlations } from "@/composables/forms/formTypes";
import type {
  BasicData,
  GasProperties
} from "@/libs/calculations/fluidProperties/d";
import { buildParams } from "../buildParams";
import type { PsuedoCriticalCalculations } from "../psuedo-criticals";
import type { LagrangeInterpolationMapWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";
import { evaluateLagrangePolynomialWithGroupings } from "@/composables/scenarioCalculations/lagrangeInterpolation";

const methodMapGV = {
  [Gas_Viscosity_Options_Enum.ChenRuth]: chenRuthGasViscosityCalculator,
  [Gas_Viscosity_Options_Enum.Lee]: leeGasViscosityCalculator,
  [Gas_Viscosity_Options_Enum.CarrKobayashiBurrows]:
    standingsDempseyGasViscosityCalculator,
  [Gas_Viscosity_Options_Enum.CarrKobayashiBurrowsDib]:
    carrKobayashiBurrowsGasViscosityCalculator
};

// const methodMapGV = {
//   [Gas_Viscosity_Options_Enum.ChenRuth]: chenRuthGasViscosityCalculator,
//   [Gas_Viscosity_Options_Enum.Lee]: leeGasViscosityCalculator,
//   [Gas_Viscosity_Options_Enum.CarrKobayashiBurrows]:
//     standingsDempseyGasViscosityCalculator
// };

// static/Fluid_Schutff_Gas_Edition.xlsx

export function getGasViscosityCalculations(
  basicData: BasicData,
  correlations: Correlations,
  psuedoCriticals: PsuedoCriticalCalculations,
  inSituPressurePSIA: number,
  temperatureF: number,
  gasMolecularWeight: number,
  gasCompressibilityFactor: number,
  gasProperties: GasProperties[],
  customFluidProperties: LagrangeInterpolationMapWithGroupings | null,
  customViscosityProperties: LagrangeInterpolationMapWithGroupings | null
): GasViscosityCalculations {
  const gasViscosityMethod =
    correlations?.gas_viscosity ||
    Gas_Viscosity_Options_Enum.CarrKobayashiBurrows;

  const gasViscosityCalculator = methodMapGV[gasViscosityMethod];

  const gasVolumeCompressibilityParams = buildParams(
    basicData,
    correlations,
    psuedoCriticals,
    inSituPressurePSIA,
    temperatureF,
    gasProperties
  );

  const {
    temperatureKelvin,
    temperatureInRankine,
    psuedoCriticals: {
      adjustedPsuedoCriticalPressurePSIA,
      adjustedPsuedoCriticalTemperatureRankine
    }
  } = gasVolumeCompressibilityParams;

  // M22: =(((D3/14.696)*D33)/(0.0821*D29*I38))/16.018
  const gasDensityLBMFT3 =
    gasCompressibilityFactor > 0
      ? ((inSituPressurePSIA / 14.696) * gasMolecularWeight) /
        (0.0821 * temperatureKelvin * gasCompressibilityFactor) /
        16.018
      : 0;

  // M17: =IF(D3>I27,1,0) ------------------- this is made into a boolean here in the code.
  const pressureAboveCritical =
    inSituPressurePSIA > adjustedPsuedoCriticalPressurePSIA;

  // M18: =IF(D27>I26,1,0) ------------------- this is made into a boolean here in the code.
  const temperatureAboveCritical =
    temperatureInRankine > adjustedPsuedoCriticalTemperatureRankine;

  // M20: =IF(AND(M18=1,M17=0),"Gaseous",IF(AND(M18=0,M17=1),"Liquid",IF(AND(M18=0,M17=0),"Vapor","Supercritical")))
  let gasState: GasState;
  if (temperatureAboveCritical && !pressureAboveCritical) {
    gasState = "Gaseous";
  } else if (!temperatureAboveCritical && pressureAboveCritical) {
    gasState = "Liquid";
  } else if (!temperatureAboveCritical && !pressureAboveCritical) {
    gasState = "Vapor";
  } else {
    gasState = "Supercritical";
  }

  const gasViscosityParams = Object.assign({}, gasVolumeCompressibilityParams, {
    gasMolecularWeight,
    gasCompressibilityFactor,
    gasDensityLBMFT3
  });

  let gasVolumeFactorCFSFCF: number = 0;
  let gasViscosityCP: number = 0;
  let intermediates;

  if (customFluidProperties?.gasFormationVolumeFactorRCFSCF != null) {
    gasVolumeFactorCFSFCF = evaluateLagrangePolynomialWithGroupings(
      customFluidProperties.gasFormationVolumeFactorRCFSCF,
      inSituPressurePSIA,
      temperatureF
    );
  } else {
    // D38: =0.0282793*(I38*D27)/(D3)
    gasVolumeFactorCFSFCF = inSituPressurePSIA
      ? (0.0282793 * gasCompressibilityFactor * temperatureInRankine) /
        inSituPressurePSIA
      : 0;
  }

  if (customViscosityProperties?.gasViscosityCP != null) {
    gasViscosityCP = evaluateLagrangePolynomialWithGroupings(
      customViscosityProperties.gasViscosityCP,
      inSituPressurePSIA,
      temperatureF
    );
    intermediates = { gasViscosityCP };
  } else {
    ({ gasViscosityCP, intermediates } =
      gasViscosityCalculator(gasViscosityParams));
  }

  gasVolumeFactorCFSFCF = Number.isFinite(gasVolumeFactorCFSFCF)
    ? gasVolumeFactorCFSFCF
    : 0;
  gasViscosityCP = Number.isFinite(gasViscosityCP) ? gasViscosityCP : 0;

  return {
    gasState,
    gasViscosityCP,
    gasDensityLBMFT3,
    gasVolumeFactorCFSFCF,
    params: gasViscosityParams,
    gasCompressibilityFactor,
    intermediates: {
      ...intermediates,
      temperatureAboveCritical,
      pressureAboveCritical
    }
  };
}
